/**
 * Reconnect behaviour shared by every socket this app opens.
 *
 * socket.io's defaults (`reconnectionDelay: 1000`, `randomizationFactor:
 * 0.5`) put the FIRST retry in [500ms, 1500ms] — ideal for one phone losing
 * signal, wrong for a fleet: when an ECS scale-in closes every socket a task
 * held, all its clients return inside one second, a synchronised herd aimed
 * at the remaining capacity. Even with startup work gated behind the READY
 * boundary (socket-recovery.ts), the connections themselves are the storm.
 *
 * The spread lives in `reconnectionDelay` + `randomizationFactor`, NOT in
 * `reconnectionDelayMax`: backoff computes delay·factor^attempt, jitters,
 * and only THEN clamps — so the max cannot bind on attempt 0 (measured:
 * raising only the max widened the worst case with no better spread).
 *
 * The trade: attempt 0 wants opposite things for a blip (instant) and a
 * fleet event (wide). 2000 ms with full jitter puts attempt 0 in ~[0, 4 s]:
 * 2,100 clients arrive over four seconds instead of one, and a lone user's
 * uniform-distributed worst case is 4 s — noticeable, not broken-looking.
 */

/**
 * Full jitter (AWS's own answer to synchronised retries): 1.0 gives
 * [0, 2×delay]; the default 0.5 still clusters densely around the base
 * delay, and the cluster is the problem.
 */
export const SOCKET_RANDOMIZATION_FACTOR = 1;

/** Base for attempt 0. See the trade above for why it is not smaller. */
export const SOCKET_RECONNECTION_DELAY_MS = 2_000;

/**
 * Ceiling from attempt 2 onward, deliberately generous: a client failing
 * repeatedly is reconnecting into something still unwell — 30 s with full
 * jitter spreads persistent retriers across half a minute.
 */
export const SOCKET_RECONNECTION_DELAY_MAX_MS = 30_000;

/**
 * The transport retry budget per reconnection cycle — finite (no retry may
 * be unbounded), sized from the delays: ~1 min over the first five attempts,
 * later ones around the 30 s ceiling ("around": jitter applies BEFORE the
 * clamp — backo2.js — so a delay can land near zero). Thirty attempts is
 * roughly 10–15 minutes, outlasting a deploy, a task replacement, and the
 * scale-out window severalfold.
 *
 * Exhaustion does NOT strand the client: the Manager resets its backoff on
 * giving up (`reconnect_failed` after `backoff.reset()`, verified in
 * manager.js) and socketRecovery.resume() kicks every disconnected socket on
 * the next foreground. While backgrounded the OS suspends timers, which is
 * what actually stops retry traffic; pause() holds only the refusal timers.
 */
export const SOCKET_RECONNECTION_ATTEMPTS = 30;

/**
 * One shared object, not three copies: agreeing options let sockets share a
 * Manager (the initial events/cue/chat trio multiplexes one WebSocket) —
 * though not permanently: on socket.io-client 4.8.3, `io()` never deletes a
 * namespace from a Manager's `nsps`, so recreating a socket for a namespace
 * the cached Manager ever held silently mints a NEW Manager. Recovery is
 * therefore scoped per Manager (socket-recovery.ts); collapsing back to one
 * physical connection is tracked as separate efficiency work.
 */
export const SOCKET_RECONNECT_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: SOCKET_RECONNECTION_ATTEMPTS,
  reconnectionDelay: SOCKET_RECONNECTION_DELAY_MS,
  reconnectionDelayMax: SOCKET_RECONNECTION_DELAY_MAX_MS,
  randomizationFactor: SOCKET_RANDOMIZATION_FACTOR,
} as const;
